/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import ArrowRight from '../../../../public/assets/white-next.svg';
import GreyNext from '../../../../public/assets/grey-next.svg';
import CourseCard from '../../test-series/mock-test/mocktest-listing/testseries-library-card';

function CourseTestSection({
  courses = [],
  className = '',
  title,
  subtitle,
  pageType = 'course',
  domainName = '/home',
  typeClasses,
}) {
  const scrollRef = useRef(null);
  const cardRefs = useRef([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (!scrollRef.current || courses.length === 0) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    if (scrollWidth > 0 && clientWidth > 0) {
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const { firstChild } = scrollRef.current;
    const cardWidth = firstChild?.offsetWidth || 358;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const itemsPerScroll = isMobile ? 1 : 3;
    const scrollAmount = isMobile
      ? cardWidth * itemsPerScroll + 20
      : cardWidth * itemsPerScroll + 30;

    if (direction === 'left') {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const updateCardHeights = () => {
    if (!cardRefs.current || cardRefs.current.length === 0) return;

    const cardElements = cardRefs.current
      .map((wrapper) => wrapper?.firstElementChild)
      .filter((el) => el);

    if (!cardElements.length) return;

    const heights = cardElements
      .map((el) => el.offsetHeight || 0)
      .filter((h) => h > 0);

    if (!heights.length) return;

    const max = Math.max(...heights);

    cardElements.forEach((el) => {
      // Directly set minHeight on the actual card element so its visual box is equalized
      // eslint-disable-next-line no-param-reassign
      el.style.minHeight = `${max}px`;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScroll();
      updateCardHeights();
    }, 100);

    const longerTimer = setTimeout(() => {
      checkScroll();
      updateCardHeights();
    }, 500);

    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
    }

    const handleResize = () => {
      setTimeout(() => {
        checkScroll();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      clearTimeout(longerTimer);
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [courses.length]);

  if (!courses || courses.length === 0) {
    return null;
  }

  return (
    <div
      className={`w-full flex mx-auto bg-[#d2e8ff30] flex-col items-stretch px-5 md:px-[52px] ${className}`}
    >
      <div className="w-full flex flex-col gap-4 sm:gap-6 py-10">
        <div className="flex flex-col w-full gap-2 mb-2">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-3">
            <div>
              {title && (
                <div className="flex items-center">
                  <div className="bg-[#ff5761] w-1.5 h-8" />
                  <p className="text-[22px]  font-medium text-black m-2">
                    {title}
                  </p>
                </div>
              )}
              {subtitle && (
                <p className="text-[28px]  font-semibold text-black">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="relative w-full flex justify-center">
          <div className="relative w-full">
            {courses.length > 3 && (
              <button
                type="button"
                onClick={() => handleScroll('left')}
                disabled={!canScrollLeft}
                className={`hidden md:inline-flex absolute -left-6 top-1/2 -translate-y-1/2 shadow p-2 z-10 ${
                  canScrollLeft ? 'bg-[#0267b1]' : 'bg-white'
                }`}
              >
                <Image
                  src={canScrollLeft ? ArrowRight : GreyNext}
                  alt="Left Arrow"
                  className="w-4 h-4 rotate-180"
                />
              </button>
            )}

            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto md:overflow-x-hidden scroll-smooth snap-x md:snap-none w-full"
            >
              {courses.map((course, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={course?.id || `course-${index}`}
                  className="shrink-0 w-full md:w-[calc((100%-30px)/3)] md:max-w-[calc((100%-30px)/3)] snap-center md:snap-start"
                >
                  <div
                    ref={(el) => {
                      if (el) {
                        cardRefs.current[index] = el;
                      }
                    }}
                    className="h-full"
                  >
                    <CourseCard
                      course={course}
                      pageType={pageType}
                      domainName={domainName}
                      typeClasses={typeClasses}
                    />
                  </div>
                </div>
              ))}
            </div>

            {courses.length > 3 && (
              <button
                type="button"
                onClick={() => handleScroll('right')}
                disabled={!canScrollRight}
                className={`hidden md:inline-flex absolute -right-4 top-1/2 -translate-y-1/2 shadow p-2 z-10 ${
                  canScrollRight ? 'bg-[#0267b1]' : 'bg-white'
                }`}
              >
                <Image
                  src={canScrollRight ? ArrowRight : GreyNext}
                  alt="Right Arrow"
                  className="w-4 h-4"
                />
              </button>
            )}

            {courses.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => handleScroll('left')}
                  disabled={!canScrollLeft}
                  className={`absolute -left-3 top-1/2 -translate-y-1/2 p-2 md:hidden rounded shadow z-10 ${
                    canScrollLeft ? 'bg-[#0267b1]' : 'bg-white'
                  }`}
                >
                  <Image
                    src={canScrollLeft ? ArrowRight : GreyNext}
                    alt="Left Arrow"
                    className="w-4 h-4 rotate-180"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => handleScroll('right')}
                  disabled={!canScrollRight}
                  className={`absolute -right-3 top-1/2 -translate-y-1/2 p-2 md:hidden rounded shadow z-10 ${
                    canScrollRight ? 'bg-[#0267b1]' : 'bg-white'
                  }`}
                >
                  <Image
                    src={canScrollRight ? ArrowRight : GreyNext}
                    alt="Right Arrow"
                    className="w-4 h-4"
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

CourseTestSection.propTypes = {
  courses: PropTypes.array,
  className: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  pageType: PropTypes.string,
  domainName: PropTypes.string,
  typeClasses: PropTypes.string,
};
export default CourseTestSection;
